import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { LOCATION_RESULTS_THEME_BG_COLOR } from '../../../../../../LocationResults.constants';
import { LocationResultsListsV2Locations } from '../../../../components';
import { type LocationResultsListsV2Props } from '../../../../LocationResultsListsV2.types';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2Expanded = memo((props: LocationResultsListsV2ExpandedProps) => {
  const {
    locationSearchActorRef,
    variation,
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    onCollapse,
  } = props;

  const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore

  // ─── Styles ──────────────────────────────────────────────────────────

  const innerContainerStyles = [
    styles.innerContainer,
    isLocationSelectionUpdateEnabled ? undefined : styles.innerContainerBackground,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.outerContainer}>
      {!isLocationSelectionUpdateEnabled ? (
        <View style={styles.shadow} needsOffscreenAlphaCompositing />
      ) : null}

      <View style={innerContainerStyles}>
        <LocationResultsListsV2Locations
          locationSearchActorRef={locationSearchActorRef}
          variation={variation}
          interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
          interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
          onViewMapPress={onCollapse}
        />
      </View>
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  innerContainer: {
    flex: 1,
  },
  innerContainerBackground: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  shadow: {
    position: 'absolute',
    height: 5,
    top: -5,
    right: 0,
    left: 0,
    zIndex: 1,

    // NOTE: `backgroundColor` is required to apply elevation on native platforms
    backgroundColor: LOCATION_RESULTS_THEME_BG_COLOR,
    ...theme.elevations['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListsV2ExpandedProps = {
  onCollapse: () => void;
} & Pick<
  LocationResultsListsV2Props,
  | 'locationSearchActorRef'
  | 'variation'
  | 'interactedLocationRestaurantSlug'
  | 'interactedLocationDeliveryAddressId'
>;
